import { ApiManager } from 'utlis/api_manager';
import { parseOverviewData } from 'utlis/parseChatOverview';

const api = new ApiManager(process.env.REACT_APP_BASE_URL, 1);
const designAuditApi = process.env.REACT_APP_DESIGN_AUDIT_BASE_URL;

export const listConversations = async (designReviewUuid, limit = 50, offset = 0) => {
  const url = 'project/conversations';
  const params = {
    design_review: designReviewUuid,
    status: 'active',
    limit,
    offset,
  };

  const { data } = await api.setUrl(url).get(params);

  return data;
};

export const getConversationMessages = async (conversationUuid, limit = 20, beforeSequence = null) => {
  const url = `project/conversations/${conversationUuid}/messages`;
  const params = { limit };

  if (beforeSequence) {
    params.before_sequence = beforeSequence;
  }

  const { data } = await api.setUrl(url).get(params);

  return data;
};

export const sendChatMessage = async ({
  designReviewUuid,
  message,
  conversationId = null,
  onDelta,
  onError,
  onDone,
  signal,
}) => {
  const token = localStorage.getItem('access_token');
  const backendUrl = process.env.REACT_APP_X_BACKEND_URL;

  const body = {
    design_review_uuid: designReviewUuid,
    message,
  };

  if (conversationId) {
    body.conversation_id = conversationId;
  }

  const response = await fetch(`${designAuditApi}/critique_chat`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-BACKEND-URL': backendUrl,
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Request failed' }));

    throw new Error(errorData.message || `HTTP ${response.status}`);
  }

  const reader = response.body.getReader();
  // eslint-disable-next-line compat/compat -- TextDecoder used for SSE stream parsing
  const decoder = new TextDecoder();
  let buffer = '';
  let assistantText = '';
  let overviewData = null;
  let source = null;
  let newConversationId = null;
  let streamDone = false;
  let onDoneCalled = false;

  const processEvent = event => {
    if (event.delta) {
      assistantText += event.delta;

      const parsedFromText = parseOverviewData(assistantText);

      if (parsedFromText) {
        overviewData = parsedFromText;

        return;
      }

      // Overview JSON is sent as one payload; don't typewriter it as markdown
      if (assistantText.trimStart().startsWith('{')) {
        return;
      }

      if (onDelta) {
        onDelta(event.delta, assistantText);
      }

      return;
    }

    if (event.error) {
      if (onError) {
        onError(event.error);
      }
    }

    if (event.source === 'overview' || event.metadata?.source === 'overview') {
      source = 'overview';
    }

    const parsedFromEvent = parseOverviewData(event.content)
      || parseOverviewData(event.overview)
      || parseOverviewData(event.data)
      || parseOverviewData(event);

    if (parsedFromEvent) {
      overviewData = parsedFromEvent;
    }

    if (event.done) {
      newConversationId = event.conversation_id;

      if (!overviewData) {
        overviewData = parseOverviewData(assistantText);
      }

      onDoneCalled = true;

      if (onDone) {
        onDone(assistantText, newConversationId, {
          overview: overviewData,
          source: source || event.source || event.metadata?.source,
        });
      }
    }
  };

  const processLine = line => {
    const normalized = line.replace(/\r$/, '');

    if (!normalized.startsWith('data: ')) {
      return;
    }

    try {
      processEvent(JSON.parse(normalized.slice(6)));
    }
    catch {
      // Skip malformed SSE lines
    }
  };

  while (!streamDone) {
    const { value, done } = await reader.read();

    if (done) {
      streamDone = true;
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');

    buffer = lines.pop();

    lines.forEach(processLine);
  }

  if (buffer.trim()) {
    processLine(buffer.trim());
  }

  if (!onDoneCalled && onDone) {
    onDone(assistantText, newConversationId, {
      overview: overviewData || parseOverviewData(assistantText),
      source,
    });
  }

  return { assistantText, conversationId: newConversationId, overview: overviewData };
};

export default {
  listConversations,
  getConversationMessages,
  sendChatMessage,
};
